// Keeps the customer dashboard live: each change from /v1/changes refreshes
// the queries it touches, so the tab beside WhatsApp updates by itself.
import { useQueryClient } from '@tanstack/react-query';
import type { Change } from '@cover/core/api/types.ts';
import { useLiveChanges } from '@cover/core/live.ts';
import { keys } from './api.ts';
import { useBusinessId } from './session.tsx';
import { chatKey } from './components/Chat.tsx';

export function useCustomerLive() {
  const qc = useQueryClient();
  const b = useBusinessId();
  return useLiveChanges({
    onReset: () => qc.invalidateQueries(),
    onChanges: (changes: Change[]) => {
      const touched = new Set<readonly unknown[]>();
      // What's next depends on everything; a new business (a sign-up telling us about their work) changes /me.
      if (changes.length) touched.add(keys.next);
      if (changes.some((ch) => ch.type === 'message' || ch.type === 'conversation')) touched.add(chatKey);
      if (changes.some((ch) => ch.type === 'business' || (ch.businessId && !b))) touched.add(keys.me);
      for (const ch of changes) {
        if (ch.businessId && ch.businessId !== b) continue;
        switch (ch.type) {
          case 'fact':
            touched.add(keys.profile(b)).add(keys.facts(b));
            break;
          case 'quote':
            touched.add(keys.quotes(b));
            break;
          case 'policy':
            touched.add(keys.policies(b)).add(keys.profile(b));
            break;
          case 'claim':
            touched.add(keys.claims(b)).add(keys.claim(ch.id)).add(keys.profile(b));
            break;
          case 'asset':
            touched.add(keys.assets(b)).add(keys.summary(b)).add(keys.profile(b));
            break;
          case 'document':
            touched.add(keys.documents(b));
            break;
          case 'connection':
            touched.add(keys.connections(b));
            break;
        }
      }
      for (const key of touched) qc.invalidateQueries({ queryKey: key });
    },
  });
}
